/**
 * TCP Onboarding — question definitions.
 *
 * EDITORIAL EDITS HAPPEN HERE.
 * The quiz UI is a thin renderer; reorder/rename/relabel anything in
 * this file without touching components or tests. Every option's `value`
 * is what lands in `users.preferences.onboarding_responses` and in the
 * `tcp_onboarding_response` events.
 *
 * Question kinds supported by `OnboardingQuizPage`:
 *   - "choice"       — pick exactly one
 *   - "multi"        — pick 1..N (max enforced by min/max)
 */

const ONBOARDING_QUESTIONS = [
    {
        id: "region",
        kind: "choice",
        // The answer here also writes to `preferences.region` directly,
        // since the backend understands uk/usa/global natively.
        writes_to_preference: "region",
        eyebrow: "Where are you reading from?",
        title: "Investigations move differently across regions.",
        helper: "We'll lightly shape the threads you see first. Change this anytime.",
        options: [
            { value: "uk", label: "United Kingdom" },
            { value: "usa", label: "United States" },
            { value: "europe", label: "Europe" },
            { value: "anz", label: "Australia & NZ" },
            { value: "global", label: "Global · everywhere" },
        ],
    },
    {
        id: "entry_point",
        kind: "choice",
        eyebrow: "What pulled you in?",
        title: "What first made you start asking questions?",
        helper: "There are no right answers here — just where curiosity began.",
        options: [
            { value: "parenthood", label: "Becoming a parent" },
            { value: "health_food", label: "Health & food" },
            { value: "tech_ai", label: "Technology & AI" },
            { value: "government_policy", label: "Government & policy" },
            { value: "environment_infra", label: "Environment & infrastructure" },
            { value: "financial_systems", label: "Financial systems" },
            { value: "media_information", label: "Media & information" },
            { value: "general_curiosity", label: "Just general curiosity" },
        ],
    },
    {
        id: "depth_mode",
        kind: "choice",
        writes_to_preference: "depth_mode",
        eyebrow: "How do you read?",
        title: "How deep do you want to go on each investigation?",
        helper: "We'll match the pacing of what we surface to you.",
        options: [
            // Backend enum is beginner | deep — the third option still
            // writes "deep" but tags `depth_intent: every_layer` in
            // onboarding_responses for editorial.
            { value: "beginner", label: "Calm overview · the essentials" },
            { value: "deep", label: "Evidence & sources · the receipts" },
            { value: "deep", aux_value: "every_layer", label: "Every layer · the full rabbit hole" },
        ],
    },
    {
        id: "interests",
        kind: "multi",
        writes_to_preference: "interests",
        min: 1,
        max: 6,
        eyebrow: "Choose your threads",
        title: "Which systems are you most curious about right now?",
        helper: "Pick up to six. Your dashboard will lead with these.",
        options: [
            { value: "food-systems", label: "Food systems" },
            { value: "pharma-incentives", label: "Pharmaceutical incentives" },
            { value: "ai-infrastructure", label: "AI infrastructure" },
            { value: "data-centres", label: "Data centres" },
            { value: "environmental-systems", label: "Environmental systems" },
            { value: "corporate-ownership", label: "Corporate ownership" },
            { value: "modern-childhood", label: "Modern childhood" },
            { value: "attention-economy", label: "Attention economy" },
            { value: "smart-infrastructure", label: "Smart infrastructure" },
            { value: "energy-systems", label: "Energy systems" },
            { value: "media-influence", label: "Media & influence" },
            { value: "digital-identity", label: "Digital identity" },
            { value: "urban-planning", label: "Urban planning" },
            { value: "public-health", label: "Public health" },
            { value: "financial-systems", label: "Financial systems" },
        ],
    },
    {
        id: "identity",
        kind: "choice",
        eyebrow: "Where do you stand?",
        title: "Which of these feels closest to how you'd describe yourself?",
        helper: "We don't show this to anyone. It just lightly shapes what we surface.",
        options: [
            { value: "curious_observer", label: "A curious observer" },
            { value: "parent", label: "A parent" },
            { value: "researcher", label: "A researcher" },
            { value: "systems_thinker", label: "A systems thinker" },
            { value: "independent_creator", label: "An independent creator" },
            { value: "health_conscious", label: "Health-conscious" },
            { value: "industry_worker", label: "Working inside an industry" },
            { value: "just_exploring", label: "Just exploring" },
        ],
    },
    {
        id: "tcp_evolution",
        kind: "multi",
        min: 1,
        max: 3,
        eyebrow: "What pulls you forward?",
        title: "What would you most like to see TCP keep building?",
        helper: "Pick up to three. This becomes part of how we prioritise.",
        options: [
            { value: "more_investigations", label: "More investigations" },
            { value: "more_timelines", label: "More timelines" },
            { value: "more_tools", label: "More maps & tools" },
            { value: "more_source_archives", label: "More source archives" },
            { value: "more_explainers", label: "More explainers" },
            { value: "more_local_context", label: "More local context" },
            { value: "more_pathways", label: "More rabbit-hole pathways" },
            { value: "more_visualisations", label: "More visualisations" },
        ],
    },
];

export default ONBOARDING_QUESTIONS;
